/**
 * **快捷键注册表** —— 从 Playground.vue 抽出来。
 *
 * ## 这个文件负责什么
 *
 * 底部提示条直接渲染这张表 —— 所以**说明和实现只有一份**，
 * 不会出现「文档写了但实际不生效」。部分条目只展示（`run` 为空），
 * 比如框选 / 平移，那些由图引擎处理。
 *
 * ## ⚠️ 抽出来的方式：函数体逐字不动
 *
 * 这张表**每一条都咬住宿主的状态或动作**（`enabled: () => hasSelection.value`
 * 之类），所以它只能整段搬、没有「先搬字面量」的切口 —— 见文档第 15 轮的调研。
 * 依赖全部由 `ctx` 注入，表格本身一个字没改。
 */
import { computed } from 'vue'
import { EDITOR_META, EDITOR_ORDER, IMPLEMENTED_EDITORS } from '@/layout/areas'
import type { EditorType } from '@/layout/areas'
import type { Shortcut } from '@/graph/shortcuts'
import type { AlignMode } from '@/graph/nodeAlign'
import type { NoticeActionInput } from './useUndoRedo'

export interface ShortcutsCtx {
  /* ── 读值的 ref / computed ── */
  selectedIds: { value: string[] }
  hasSelection: { value: boolean }
  activeNodeId: { value: string | null }
  socketTip: { value: unknown }
  levelPath: { value: { nodeId: string; title: string }[] }
  lassoMode: { value: unknown }
  historyState: { value: { canUndo: boolean; canRedo: boolean; undo?: string; redo?: string } }
  clipboardState: { value: { canCopy: boolean; canPaste: boolean } }

  /* ── 动作 ── */
  doUndo: () => void
  doRedo: () => void
  doCopy: () => void
  doPaste: () => void
  groupSelection: () => void
  ungroupSelection: () => void
  /** 按 id 找节点模型（判「选中的是不是组」用） */
  modelOf: (id: string) => { type: string } | undefined
  selectAll: () => void
  toggleHideUnused: () => void
  closePanels: () => void
  targetNodesForToggle: () => string[]
  deleteSelectedNodes: (mode?: 'frame-only' | 'with-children') => void
  exitSubgraph: () => void
  openAddMenu: (point?: { x: number; y: number }) => void
  /* ── 工具栏要用、但键盘没有对应键的动作 ── */
  doAlign: (mode: AlignMode) => void
  canAlign: (count: number, mode: AlignMode) => boolean
  wrapSelectionInFrame: () => void
  zoomIn: () => void
  zoomOut: () => void
  fitView: () => void
  /**
   * 把**焦点窗口**换成某个编辑器（`Alt + 1..9`）。
   *
   * ⚠️ 只在焦点窗口上生效（和点那个窗口头部的菜单是同一个动作）——
   * 快捷键作用于"当前窗口"是这个编辑器的既有约定（Ctrl+H / Delete 都这样）。
   */
  setFocusedEditor: (editor: EditorType) => void
}

export function useShortcuts(ctx: ShortcutsCtx) {
  const {
    selectedIds,
    hasSelection,
    activeNodeId,
    socketTip,
    levelPath,
    lassoMode,
    historyState,
    clipboardState,
    doUndo,
    doRedo,
    doCopy,
    doPaste,
    groupSelection,
    ungroupSelection,
    modelOf,
    selectAll,
    toggleHideUnused,
    closePanels,
    targetNodesForToggle,
    deleteSelectedNodes,
    exitSubgraph,
    openAddMenu,
    doAlign,
    canAlign,
    wrapSelectionInFrame,
    zoomIn,
    zoomOut,
    fitView,
    setFocusedEditor,
  } = ctx

const shortcuts = computed<Shortcut[]>(() => [
  // ── 有上下文才出现 ──
  {
    id: 'hide-unused',
    label: '隐藏 / 显示未连接的端口',
    short: '端口',
    combos: [{ key: 'h', ctrl: true }],
    enabled: () => targetNodesForToggle().length > 0,
    run: toggleHideUnused,
  },
  /*
   * ⚠️ 这里原来还有一条**纯展示**的 `{ id: 'delete', combos: [Delete], 没有 run }`。
   *
   * 它的存在理由是"提示条里显示一句 Delete 的说明"，而在 Vue Flow 时代
   * 删除是**引擎**做的（`:delete-key-code`），所以它不需要 `run`。
   * 画布换成我们自己的之后引擎不删了，而它**排在三条真删除动作前面**，
   * 于是把 Delete 挡死 —— 真机反馈：「选中节点 Delete 没反应」。
   *
   * 两处都修了：
   *   · 机制层：`findShortcut` 现在跳过没有 `run` 的条目（见那里的注释）；
   *   · 这一条本身删掉 —— 提示条已经由下面的 `delete-nodes` 显示同一个键，
   *     留着它就是同一个键两行提示。
   */
  {
    // Esc 退出一层。放在最前面 —— 有子图层级时它该最先响应。
    id: 'exit-subgraph',
    label: '退出一层',
    short: '退出',
    combos: [{ key: 'Escape' }],
    enabled: () => levelPath.value.length > 0,
    run: () => exitSubgraph(),
  },
  {
    id: 'group-selection',
    label: '打成组',
    short: '成组',
    combos: [{ key: 'g', ctrl: true }],
    enabled: () => selectedIds.value.length > 0,
    run: groupSelection,
    /*
     * ⚠️ 这一条**同时**在提示条和工具栏出现（`Ctrl+G` 值得记），
     * 所以不设 `statusBar: false` —— 这正是两个字段分开的原因：
     * 「有键盘快捷键」和「工具栏上有没有按钮」是两件独立的事。
     */
    toolbar: { section: 'context', icon: '<rect x="3" y="3" width="8" height="8" rx="1"/><rect x="13" y="13" width="8" height="8" rx="1"/><path d="M11 7h4a2 2 0 0 1 2 2v4"/>' },
  },
  {
    /*
     * ★ 解组：和「打成组」互为逆操作，所以快捷键也成对
     * （`Ctrl+G` / `Ctrl+Shift+G`，和 Blender 的 `Ctrl+Alt+G` 不同，
     *  但这条链路上 Shift 更顺手、也不和已有的撞）。
     *
     * ⚠️ 只有选中**组**时才 enabled —— 否则它会出现在提示条上
     * 却按了没反应。
     */
    id: 'ungroup-selection',
    label: '解组',
    short: '解组',
    combos: [{ key: 'g', ctrl: true, shift: true }],
    enabled: () => selectedIds.value.some((id) => modelOf(id)?.type === 'group'),
    run: () => ungroupSelection(),
  },
  {
    id: 'add-node',
    label: '添加节点',
    short: '＋ 添加',
    combos: [{ key: 'a', shift: true }],
    run: () => openAddMenu(),
  },
  {
    id: 'undo',
    label: '撤销',
    short: '↶ 撤销',
    combos: [{ key: 'z', ctrl: true }],
    enabled: () => historyState.value.canUndo,
    run: doUndo,
  },
  {
    id: 'redo',
    label: '重做',
    short: '↷ 重做',
    combos: [
      { key: 'z', ctrl: true, shift: true },
      { key: 'y', ctrl: true },
    ],
    enabled: () => historyState.value.canRedo,
    run: doRedo,
  },
  {
    id: 'copy',
    label: '复制节点',
    short: '复制',
    combos: [{ key: 'c', ctrl: true }],
    enabled: () => clipboardState.value.canCopy,
    run: doCopy,
  },
  {
    id: 'paste',
    label: '粘贴节点',
    short: '粘贴',
    combos: [{ key: 'v', ctrl: true }],
    enabled: () => clipboardState.value.canPaste,
    run: doPaste,
  },
  {
    id: 'delete-nodes',
    label: '删除节点',
    short: '删除',
    combos: [{ key: 'delete' }, { key: 'backspace' }],
    enabled: () => selectedIds.value.length > 0,
    // 选中里有框时默认只删框，提示条里再给「连子节点一起删」的选择
    run: () => deleteSelectedNodes('frame-only'),
  },
  {
    id: 'delete-with-children',
    label: '删除（含框内节点）',
    combos: [
      { key: 'delete', shift: true },
      { key: 'backspace', shift: true },
    ],
    enabled: () => selectedIds.value.length > 0,
    run: () => deleteSelectedNodes('with-children'),
  },
  {
    id: 'delete-frame-only',
    label: '只删框（留下节点）',
    combos: [
      { key: 'delete', alt: true },
      { key: 'backspace', alt: true },
    ],
    enabled: () => selectedIds.value.length > 0,
    run: () => deleteSelectedNodes('frame-only'),
  },
  {
    id: 'select-all',
    label: '全选',
    short: '全选',
    combos: [{ key: 'a', ctrl: true }],
    run: selectAll,
  },
  {
    id: 'close',
    label: '关掉面板 / 浮层',
    short: '关闭',
    combos: [{ key: 'escape' }],
    enabled: () =>
      activeNodeId.value !== null ||
      socketTip.value !== null ||
      lassoMode.value !== null,
    run: closePanels,
  },

  // ── 常态手势 ──
  {
    id: 'box-select',
    label: '框选',
    display: '左键拖空白',
    touchDisplay: '单指拖空白',
  },
  { id: 'multi-select', label: '加选', display: '⌘/Ctrl/Shift + 点击' },
  /*
   * ⚠️ 触摸端的拖动语义**改过**：
   *   单指拖动现在默认是「平移」（左下角的手形按钮亮着 = 移动）；
   *   按一下那个按钮切到「框选」，此时单指拖动 = 框选（缩放暂时不可用）。
   * 提示文案必须跟着改，否则底部提示条会教错人。
   */
  /*
   * ⚠️ `display` 里**只写键鼠**，`touchDisplay` 里**只写触摸** ——
   * 真机反馈：「提示条里快捷键键鼠和触摸两种混杂出现，而不是按对应模式显示」。
   * 那条「滚轮 / 捏合」就是混杂：桌面模式下不该出现"捏合"。
   * 判据由 `mount-app` 的一条断言守着（桌面模式下不许出现触摸话术）。
   */
  {
    id: 'pan',
    label: '平移画布',
    /*
     * ⚠️ 这里原来写着「中键拖 / 空格 + 左键拖」—— **空格那半条是假的**。
     * 空格平移在引擎时代就被删了（`useHotkeys.onSpaceKey` 的注释里写着
     * 「从来没生效过」），而我写新画布时又照着旧文案抄了一遍。
     * 现在画布只认中键（以及触摸端由宿主打开的平移模式）。
     */
    display: '中键拖',
    touchDisplay: '单指拖（左下「移动」亮起时）',
  },
  {
    id: 'marquee',
    label: '累加框选',
    display: 'Shift + 左键拖',
    touchDisplay: '按左下切到「框选」后单指拖',
  },
  /*
   * ⚠️ `display` 只写「滚轮」：捏合是触摸手势，归 `touchDisplay`。
   */
  { id: 'zoom', label: '缩放画布', display: '滚轮', touchDisplay: '双指捏合（框选模式下不可用）' },
  { id: 'reroute', label: '改接', display: '从已连线的输入拖出', touchDisplay: '从已连线的输入拖出' },
  { id: 'cut-links', label: '划过剪断连线', display: 'Alt + 左键拖' },
  { id: 'mute-links', label: '划过静音连线', display: 'Alt + Shift + 左键拖' },
  { id: 'touch-menu', label: '菜单', display: '', touchDisplay: '长按空白 / 节点 / 连线' },
  { id: 'touch-enter', label: '进入子图', display: '', touchDisplay: '双击节点主体' },

  /* ══════════════════════════════════════════════════════════════
     切窗口类型：`Alt + 1..9`
     ══════════════════════════════════════════════════════════════

     ⚠️ **两条 combo 上的坑，都踩过**：

     ① **不能用 `Ctrl + 数字`**（设计文档里原本是这么写的，"Blender 的
        Ctrl+1..9 思路"）：在 Chrome / Edge 的 Windows·Linux 上
        `Ctrl+1..8` 是**切标签页**，浏览器自己吃掉，页面根本收不到 ——
        症状是"按了没反应"，而且只在有那么多标签页的机器上复现。
     ② **`Alt + 数字` 在 macOS 上 `event.key` 是 `'¡'`** 这类死键字符，
        按 `key` 匹配永远匹配不上。所以这几条用 `code: 'Digit1'`
        （物理键位）匹配 —— 见 `ShortcutCombo.code`。

     ⚠️ 它们**不进底部提示条**（`statusBar: false`）：9 条会在那条窄带上
     挤掉真正要记的键位。发现入口是「系统 → 帮助」的窗口那一组。

     ⚠️ 切的是**焦点窗口**（`focusedEditor` / `setFocusedEditor`），
     和点窗口头部那个菜单是同一个动作 —— 手动切只改"当前"，
     不动窗口的默认身份（见 `AreaLeaf.defaultEditor`）。

     ⚠️⚠️ **只有前 9 个编辑器有快捷键**（下面那个 `slice(0, 9)`）：
     数字键只有九个，而编辑器总数早就超过九个了（内核 8 条 + 包贡献的）。
     排在第 10 位及以后的**没有 Alt+数字**，只能走窗口类型菜单 ——
     这是容量限制，不是 bug；但**加内核编辑器 / 改 digit 时要意识到
     它会把某个包贡献的编辑器挤出快捷键范围**（2027 加「文档」那一条时
     就把夹具的编辑器挤出去过一次，`verify:render` 的 Alt+数字那条当场抓到）。
     */
  ...EDITOR_ORDER.slice(0, 9).map((editor, index) => ({
    id: `editor-${index + 1}`,
    label: `切到「${EDITOR_META[editor].label}」`,
    short: `${index + 1}`,
    combos: [{ key: `${index + 1}`, code: `Digit${index + 1}`, alt: true }],
    statusBar: false,
    /* 没实现的编辑器选不了（菜单里也是灰的），所以按键也不该有反应 */
    enabled: () => IMPLEMENTED_EDITORS.includes(editor),
    run: () => setFocusedEditor(editor),
  })),

  /* ══════════════════════════════════════════════════════════════
     工具栏专属命令
     ══════════════════════════════════════════════════════════════════

     ⚠️ 这些**没有 `combos`**（键盘上没绑键），也设了 `statusBar: false`
     （不进底部提示条）—— 它们只出现在左侧工具栏。
     ⚠️ 用户实测后要求「去掉多余的中间按钮，暂时保留成组和框住」，
     所以这一段现在**只有 2 条**（成组、框住）。

     但它们的 label / enabled / run 仍然和别的条目**同源**：
     工具栏不自己写一份「什么时候可用」的判断，只读 `enabled()`。
     这样以后给「左对齐」绑一个快捷键，只要加 `combos`，
     键盘和按钮会自动一致，不需要改工具栏。
     ══════════════════════════════════════════════════════════════════ */

  /*
   * ⚠️ 这里原来有 8 条「对齐 / 分布」（align-left / hcenter / …）。
   * **已删除**：用户实测后要求工具栏中段「暂时保留成组和框住」。
   *
   * 它们本来在右键菜单的「排列 → 对齐 / 等间隙分布」里就有一份实现
   * （见 `useMenus` 的 `align()` 助手），这里那 8 条只是为了**工具栏按钮**
   * 才登记的。既然按钮撤了，这份登记就是纯粹的重复 ——
   * 删掉，别留 8 个没有任何入口能触发的死条目。
   */

  /* ── 用框圈住选中 ── */
  {
    id: 'wrap-frame',
    label: '用框圈住选中节点',
    short: '框住',
    statusBar: false,
    toolbar: { section: 'context', icon: '<rect x="3" y="3" width="18" height="18" rx="2" stroke-dasharray="4 3"/><rect x="8" y="8" width="8" height="8" rx="1"/>' },
    enabled: () => selectedIds.value.length > 0,
    run: wrapSelectionInFrame,
  },

  /* ── 视图（不改数据，只改看的方式）── */
  {
    id: 'zoom-in',
    label: '放大',
    statusBar: false,
    toolbar: { section: 'view', icon: '<circle cx="11" cy="11" r="7"/><path d="M11 8v6M8 11h6M20 20l-3.5-3.5"/>' },
    run: zoomIn,
  },
  {
    id: 'zoom-out',
    label: '缩小',
    statusBar: false,
    toolbar: { section: 'view', icon: '<circle cx="11" cy="11" r="7"/><path d="M8 11h6M20 20l-3.5-3.5"/>' },
    run: zoomOut,
  },
  {
    id: 'fit-view',
    label: '适配画面',
    statusBar: false,
    toolbar: { section: 'view', icon: '<path d="M4 9V5a1 1 0 0 1 1-1h4M15 4h4a1 1 0 0 1 1 1v4M20 15v4a1 1 0 0 1-1 1h-4M9 20H5a1 1 0 0 1-1-1v-4"/>' },
    run: fitView,
  },
  /*
   * ⚠️ 这里原来还有一条 `zoom-to-selection`「缩放到选中」。
   * **已删除** —— 用户实测：「缩放到选中按钮没什么用，可以删除」。
   * 选中之后本来就会跟随（活动节点变化时图会 fit），
   * 再单独给一个按钮只是多一个要记的东西。
   */
])

  return { shortcuts }
}
